import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import {
  stockHistoryService,
  articleService,
  depotService,
  DepotView,
  MouvementStockView,
  OrigineMouvement,
} from '../../api/services';
import { Article } from '../../types';

const ORIGINE_LABEL: Record<OrigineMouvement, string> = {
  DOCUMENT: 'Document',
  VENTE: 'Vente',
  ACHAT: 'Achat',
  AJUSTEMENT: 'Ajustement',
  INVENTAIRE: 'Inventaire',
  TRANSFERT: 'Transfert',
};

const ORIGINE_STYLE: Record<OrigineMouvement, string> = {
  DOCUMENT: 'bg-gray-100 text-gray-700',
  VENTE: 'bg-blue-100 text-blue-800',
  ACHAT: 'bg-indigo-100 text-indigo-800',
  AJUSTEMENT: 'bg-purple-100 text-purple-800',
  INVENTAIRE: 'bg-teal-100 text-teal-800',
  TRANSFERT: 'bg-amber-100 text-amber-800',
};

type ModalMode = 'mouvement' | 'inventaire' | null;

const StockMovements: React.FC = () => {
  const [mouvements, setMouvements] = useState<MouvementStockView[]>([]);
  const [rupture, setRupture] = useState<any[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [busy, setBusy] = useState(true);

  const [modal, setModal] = useState<ModalMode>(null);
  const [articleId, setArticleId] = useState<number | ''>('');
  const [sens, setSens] = useState<'IN' | 'OUT'>('IN');
  const [quantite, setQuantite] = useState('');
  const [coutUnitaire, setCoutUnitaire] = useState('');
  const [stockReel, setStockReel] = useState('');
  const [motif, setMotif] = useState('');
  const [saving, setSaving] = useState(false);
  // TF-070/071 — dépôt + lot/série/DLC du mouvement manuel
  const [depots, setDepots] = useState<DepotView[]>([]);
  const [depotId, setDepotId] = useState('');
  const [numeroLot, setNumeroLot] = useState('');
  const [numeroSerie, setNumeroSerie] = useState('');
  const [dlc, setDlc] = useState('');

  const stockArticles = useMemo(
    () => articles.filter((a) => !['HEURE', 'JOUR', 'FORFAIT', 'SERVICE'].includes(a.unite as string)),
    [articles],
  );

  const load = () => {
    setBusy(true);
    Promise.all([
      stockHistoryService.history(),
      stockHistoryService.rupture(),
      articleService.getAll({ size: 1000 }),
    ])
      .then(([h, r, a]) => {
        setMouvements(h);
        setRupture(r);
        setArticles(a.content);
      })
      .catch((e) => toast.error(e?.response?.data?.message || 'Erreur'))
      .finally(() => setBusy(false));
  };

  useEffect(load, []);
  useEffect(() => { depotService.list().then(setDepots).catch(() => {}); }, []);

  const openModal = (mode: ModalMode) => {
    setArticleId('');
    setSens('IN');
    setQuantite('');
    setCoutUnitaire('');
    setStockReel('');
    setMotif('');
    setDepotId('');
    setNumeroLot('');
    setNumeroSerie('');
    setDlc('');
    setModal(mode);
  };

  const submit = async () => {
    if (articleId === '') {
      toast.error('Sélectionnez un article');
      return;
    }
    setSaving(true);
    try {
      if (modal === 'mouvement') {
        await stockHistoryService.createMovement({
          articleId: Number(articleId),
          sens,
          quantite: Number(quantite),
          coutUnitaire: sens === 'IN' && coutUnitaire ? Number(coutUnitaire) : undefined,
          depotId: depotId === '' ? null : Number(depotId),
          numeroLot: numeroLot || undefined,
          numeroSerie: numeroSerie || undefined,
          dlc: dlc || undefined,
          motif: motif || undefined,
        });
        toast.success('Mouvement enregistré');
      } else if (modal === 'inventaire') {
        await stockHistoryService.createInventaire({
          articleId: Number(articleId),
          stockReel: Number(stockReel),
          motif: motif || undefined,
        });
        toast.success('Inventaire enregistré');
      }
      setModal(null);
      load();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Erreur lors de l’enregistrement');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Stock</h1>
          <p className="text-sm text-gray-500">Mouvements, inventaire et alertes rupture.</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => openModal('mouvement')}
            className="px-3 py-2 text-sm font-medium rounded-lg bg-indigo-600 text-white hover:bg-indigo-700"
          >
            Nouveau mouvement
          </button>
          <button
            onClick={() => openModal('inventaire')}
            className="px-3 py-2 text-sm font-medium rounded-lg bg-teal-600 text-white hover:bg-teal-700"
          >
            Inventaire
          </button>
        </div>
      </div>

      {rupture.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
          <h2 className="text-sm font-semibold text-amber-900 mb-2">Articles en rupture ({rupture.length})</h2>
          <ul className="text-sm text-amber-800 space-y-1">
            {rupture.map((a) => (
              <li key={a.id}>
                <strong>{a.reference}</strong> — {a.designation} &middot; stock {a.stockActuel} &lt; min {a.stockMinimum}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <h2 className="text-sm font-semibold px-4 pt-4">Derniers mouvements</h2>
        {busy ? (
          <p className="p-4 text-sm text-gray-500">Chargement…</p>
        ) : mouvements.length === 0 ? (
          <p className="p-4 text-sm text-gray-500">Aucun mouvement enregistré.</p>
        ) : (
          <table className="min-w-full divide-y divide-gray-200 mt-2">
            <thead className="bg-gray-50 text-xs text-gray-500 uppercase">
              <tr>
                <th className="px-4 py-3 text-left">Date</th>
                <th className="px-4 py-3 text-left">Sens</th>
                <th className="px-4 py-3 text-left">Origine</th>
                <th className="px-4 py-3 text-left">Article</th>
                <th className="px-4 py-3 text-right">Quantité</th>
                <th className="px-4 py-3 text-right">Coût unit.</th>
                <th className="px-4 py-3 text-left">Document</th>
                <th className="px-4 py-3 text-left">Motif</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 text-sm">
              {mouvements.map((m) => (
                <tr key={m.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-xs text-gray-500">{new Date(m.createdAt).toLocaleString()}</td>
                  <td className="px-4 py-3">
                    <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${
                      m.sens === 'IN' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {m.sens === 'IN' ? '↑ Entrée' : '↓ Sortie'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {m.origine && (
                      <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${ORIGINE_STYLE[m.origine]}`}>
                        {ORIGINE_LABEL[m.origine]}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm font-medium">{m.articleReference}</div>
                    <div className="text-xs text-gray-500">{m.articleDesignation}</div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono">{m.quantite}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-gray-500">
                    {m.coutUnitaire != null ? Number(m.coutUnitaire).toFixed(3) : '—'}
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500">{m.documentNumero || '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{m.motif || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6 space-y-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {modal === 'mouvement' ? 'Nouveau mouvement de stock' : 'Inventaire physique'}
            </h3>

            <label className="block text-sm">
              <span className="text-gray-700">Article</span>
              <select
                value={articleId}
                onChange={(e) => setArticleId(e.target.value ? Number(e.target.value) : '')}
                className="mt-1 w-full rounded-lg border-gray-300 text-sm"
              >
                <option value="">— Sélectionner —</option>
                {stockArticles.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.reference || a.code} — {a.designation} (stock {a.stockActuel ?? 0})
                  </option>
                ))}
              </select>
            </label>

            {modal === 'mouvement' ? (
              <>
                <label className="block text-sm">
                  <span className="text-gray-700">Sens</span>
                  <select
                    value={sens}
                    onChange={(e) => setSens(e.target.value as 'IN' | 'OUT')}
                    className="mt-1 w-full rounded-lg border-gray-300 text-sm"
                  >
                    <option value="IN">Entrée (+)</option>
                    <option value="OUT">Sortie (−)</option>
                  </select>
                </label>
                <label className="block text-sm">
                  <span className="text-gray-700">Quantité</span>
                  <input
                    type="number" min="0" step="0.001" value={quantite}
                    onChange={(e) => setQuantite(e.target.value)}
                    className="mt-1 w-full rounded-lg border-gray-300 text-sm"
                  />
                </label>
                {sens === 'IN' && (
                  <label className="block text-sm">
                    <span className="text-gray-700">Coût unitaire (optionnel — recalcule le CMUP)</span>
                    <input
                      type="number" min="0" step="0.001" value={coutUnitaire}
                      onChange={(e) => setCoutUnitaire(e.target.value)}
                      className="mt-1 w-full rounded-lg border-gray-300 text-sm"
                    />
                  </label>
                )}
                <label className="block text-sm">
                  <span className="text-gray-700">Dépôt</span>
                  <select
                    value={depotId} onChange={(e) => setDepotId(e.target.value)}
                    className="mt-1 w-full rounded-lg border-gray-300 text-sm"
                  >
                    <option value="">Dépôt principal</option>
                    {depots.map((d) => <option key={d.id} value={d.id}>{d.nom}</option>)}
                  </select>
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <label className="block text-sm">
                    <span className="text-gray-700">Lot</span>
                    <input
                      type="text" value={numeroLot} onChange={(e) => setNumeroLot(e.target.value)}
                      className="mt-1 w-full rounded-lg border-gray-300 text-sm" placeholder="Optionnel"
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="text-gray-700">N° série</span>
                    <input
                      type="text" value={numeroSerie} onChange={(e) => setNumeroSerie(e.target.value)}
                      className="mt-1 w-full rounded-lg border-gray-300 text-sm" placeholder="Optionnel"
                    />
                  </label>
                  <label className="block text-sm">
                    <span className="text-gray-700">DLC</span>
                    <input
                      type="date" value={dlc} onChange={(e) => setDlc(e.target.value)}
                      className="mt-1 w-full rounded-lg border-gray-300 text-sm"
                    />
                  </label>
                </div>
              </>
            ) : (
              <label className="block text-sm">
                <span className="text-gray-700">Stock réel compté</span>
                <input
                  type="number" min="0" step="0.001" value={stockReel}
                  onChange={(e) => setStockReel(e.target.value)}
                  className="mt-1 w-full rounded-lg border-gray-300 text-sm"
                />
              </label>
            )}

            <label className="block text-sm">
              <span className="text-gray-700">Motif</span>
              <input
                type="text" value={motif} onChange={(e) => setMotif(e.target.value)}
                className="mt-1 w-full rounded-lg border-gray-300 text-sm"
                placeholder="Optionnel"
              />
            </label>

            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => setModal(null)}
                className="px-3 py-2 text-sm rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={submit}
                disabled={saving}
                className="px-3 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
              >
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockMovements;
